/**
 * The usual particulars for each transaction type — what a line of a disbursement request is
 * commonly FOR, offered as a dropdown on the request form.
 *
 * **Suggestions, never a rule.** The cell is `<TdSelect allowCustom>` with no create handler,
 * the stock adjustment reason's shape: a particular is free text the reader may always type in
 * their own words, and nothing on the server reads or checks it against this list. It exists
 * because a box that opens empty makes every clerk invent the vocabulary one line at a time.
 *
 * **Keyed by the transaction type's NAME, case-insensitively**, because the types are a catalog
 * an administrator maintains (Cash Division → Settings) and their ids differ per database. The
 * cost is stated rather than discovered: a type somebody RENAMES or ADDS has no suggestions and
 * the cell falls back to plain typing. The day the division wants to maintain these lists
 * itself, they become rows under the transaction type — not a longer constant here.
 *
 * **Payroll is deliberately absent**, as it is from the seeded types: payroll does not run
 * through the request flow. The order is roughly how often each comes up, so the list is not
 * sorted alphabetically.
 */
const SUGGESTIONS: Record<string, readonly string[]> = {
  benefits: [
    'Mid-Year Bonus',
    'Year-End Bonus and Cash Gift',
    'Productivity Enhancement Incentive (PEI)',
    'Performance-Based Bonus (PBB)',
    'Clothing / Uniform Allowance',
    'Monetization of Leave Credits',
    'Terminal Leave Benefits',
    'Loyalty Award',
    'Hazard Pay',
    'Subsistence Allowance',
    'Laundry Allowance',
    'Overtime Pay',
  ],
  'operational expenses': [
    'Office Supplies',
    'Fuel, Oil and Lubricants',
    'Repairs and Maintenance',
    'Local Travel Expenses',
    'Training and Seminar Expenses',
    'Representation Expenses',
    'Printing and Publication',
    'Rental of Office Space / Equipment',
    'Janitorial Services',
    'Security Services',
    'Subscription Expenses',
    'Advertising Expenses',
  ],
  reimbursements: [
    'Transportation / Travel Fare',
    'Per Diem',
    'Training / Seminar Registration Fee',
    'Fuel',
    'Parking and Toll Fees',
    'Meals during Official Function',
    'Office Supplies (out of pocket)',
    'Communication Expenses',
    'Medical Expenses',
  ],
  bills: [
    'Electricity',
    'Water',
    'Internet',
    'Telephone (Landline)',
    'Mobile Phone',
    'Postage and Courier',
    'Office Rent',
    'Insurance Premium',
  ],
  'cheque issuance': [
    'Payment to Supplier / Creditor',
    "Contractor's Progress Billing",
    'Release of Retention Money',
    'Refund of Bid / Performance Security',
    'Cash Advance',
    'Replacement of Stale / Spoiled Cheque',
  ],
  'fidelity bonds': [
    'Premium for New Bond (Accountable Officer)',
    'Renewal of Fidelity Bond',
    'Increase in Bond Coverage',
  ],
};

/** The usual particulars for a transaction type, by its name; empty for a type with none. */
export function particularsSuggestions(transactionTypeName: string | null | undefined): readonly string[] {
  return transactionTypeName == null ? [] : SUGGESTIONS[transactionTypeName.trim().toLowerCase()] ?? [];
}
